import moment from "moment"
import {saveAs} from "file-saver"
import store from "@/store"
import {getIconStringWithOrigin} from "@/utils/filters"
import {GEEleType} from "@/views/GraphExplorer/Components/Drawer/GEEleType";

/**
 * High-resolution PNG export for the Graph Explorer.
 *
 * The Graph Explorer draws node labels, transaction metadata and annotations as HTML
 * overlays (cytoscape-node-html-label / cytoscape-dom-node, see UseGELayers.js). `cy.png()`
 * renders only the Cytoscape *canvas* layers, so those overlays are absent from the exported
 * image (only edge value labels, which are native, survive).
 *
 * To produce an image equivalent to what is on screen we temporarily mirror the overlay
 * text onto the canvas — node labels for addresses / transactions / merge nodes, standalone
 * note nodes, plus a grey rounded box (matching the .noteContent overlay) above each
 * annotated node — render the whole graph with `cy.png({full: true})`, then revert every
 * change. Nothing is persisted to the saved graph.
 *
 * NOTE: this duplicates the label formatting kept in UseGELayers.js. A cleaner long-term
 * fix is to render labels natively on the canvas (so export == live by construction);
 * tracked in the related issue.
 */
export default function (graphSettings) {

    // marks the temporary "note box" helper nodes we add during export
    const NOTE_FLAG = "_geExportNote"

    // label icon (address-card / owner) sizing. The label is shifted right by ICON_MARGIN so the
    // icon + text read as one unit centred on the node, exactly like the live overlay does.
    const ICON_SIZE = 16
    const ICON_GAP = 4
    const ICON_MARGIN = (ICON_SIZE + ICON_GAP) / 2

    // --- resolution -----------------------------------------------------------
    // Node labels are drawn on the canvas at NATIVE_LABEL_PX (see LABEL_STYLE). We scale the
    // whole graph so a label reaches TARGET_LABEL_PX in the exported image — the size 12pt text
    // has when printed at 300 DPI (12/72 in × 300 = 50 px), comfortably readable on a report
    // exhibit. We do NOT scale beyond that (extra pixels only bloat the file), so simple/small
    // graphs export small and only large graphs approach the size cap. Tune readability vs file
    // size via TARGET_LABEL_PX (e.g. 60 = 12pt @ 360 DPI for extra-crisp print).
    const NATIVE_LABEL_PX = 20
    const TARGET_LABEL_PX = 50
    const READABILITY_SCALE = TARGET_LABEL_PX / NATIVE_LABEL_PX   // 2.5×

    // Browser <canvas> hard limits: Chromium allows ~32767 px/side and 2^28 (≈268M) px².
    // Report exports are produced in Chromium, so cap a side just under the per-side limit and
    // keep total area safely below 2^28. NOTE: Firefox caps area near 1.24e8 px², so exports
    // larger than that are Chromium-only.
    const MAX_SIDE = 32000
    const MAX_AREA = 2.5e8

    const truncate = (value, head, tail) =>
        value && value.length > head + tail ? `${value.substring(0, head)}…${value.slice(-tail)}` : (value ?? "")

    const stripHtml = (html) => {
        // Parse inertly: note text can come from another user's saved graph, and an innerHTML
        // assignment (even on a detached node) would fire e.g. <img onerror=...>. DOMParser does not.
        const doc = new DOMParser().parseFromString(String(html ?? ""), "text/html")
        return (doc.body.textContent || "").trim()
    }

    // --- label builders (mirror UseGELayers.js templates) ---------------------
    const addressLabel = (data) => {
        const addressData = data.typeSpecific?.addressData ?? {}
        const identity = addressData.label || addressData.identities?.[0]?.label
        const owner = addressData.owner?.name || addressData.owners?.[0]?.name
        const address = addressData.addr ?? addressData.address ?? data.label ?? ""
        return identity || owner || truncate(address, 6, 4)
    }

    const transactionLabel = (data) => {
        const typeSpecific = data.typeSpecific ?? {}
        const lines = [`${typeSpecific.inputs?.length ?? 0}:${typeSpecific.outputs?.length ?? 0}`]
        if (graphSettings?.value?.showTxTimeStamp && typeSpecific.blockTime)
            lines.push(moment.unix(typeSpecific.blockTime).format("YYYY-MM-DD HH:mm:ss"))
        lines.push(truncate(data.id, 4, 3))
        return lines.join("\n")
    }

    const nodeLabel = (node) => {
        switch (node.data("type")) {
            case GEEleType.NodeAddress:
                return addressLabel(node.data())
            case GEEleType.NodeTransaction:
                return transactionLabel(node.data())
            case GEEleType.NodeMerge:
                return node.data("customLabel") ?? "Custom node"
            default:
                return ""
        }
    }

    // --- category badge (mirrors the coloured icon circle drawn as an HTML overlay) -----------
    // The overlay draws categories[0] as a coloured round .category-container with an MDI/FA icon
    // (createCategoryIcon in UseGELayers.js). cy.png can't capture that overlay, so we redraw it
    // as a small ellipse helper node whose label is the icon glyph. The glyph + its font are read
    // off the icon class's ::before pseudo-element at runtime (the icon fonts are already loaded by
    // the app), so the canvas renders the real symbol rather than a blank circle.
    const _iconCache = new Map()
    const probeIcon = (iconClass) => {
        if (_iconCache.has(iconClass)) return _iconCache.get(iconClass)
        const probe = document.createElement("i")
        probe.className = iconClass
        probe.style.cssText = "position:absolute;left:-9999px;top:-9999px;visibility:hidden"
        document.body.appendChild(probe)
        const cs = getComputedStyle(probe, "::before")
        const result = {glyph: "", fontFamily: cs.fontFamily, fontWeight: cs.fontWeight}
        const content = cs.content
        document.body.removeChild(probe)
        if (content && content !== "none" && content !== "normal") {
            result.glyph = content.replace(/^["']|["']$/g, "")
        }
        _iconCache.set(iconClass, result)
        return result
    }
    const categoryBadge = (node) => {
        const category = node.data("typeSpecific")?.addressData?.categories?.[0]
        if (!category?.icon) return null
        const {glyph, fontFamily, fontWeight} = probeIcon(getIconStringWithOrigin(category.icon))
        if (!glyph) return null
        const radius = node.width() / 2
        const position = node.position()
        return {
            id: `${NOTE_FLAG}::cat::${node.id()}`,
            x: position.x - radius * 0.7,      // lower-left, on the node's perimeter (~7–8 o'clock), like the overlay
            y: position.y + radius * 0.7,
            size: node.width() * 0.34,         // ~⅓ of the node, matching how the fixed overlay badge reads
            glyph, fontFamily, fontWeight,
            bg: category.color || "#f0f0f0",
            fg: category.secondaryColor || "#ffffff",
        }
    }

    // --- temporary canvas styles ----------------------------------------------
    // Monospace, matching the live .fontMonospace labels.
    const LABEL_FONT_FAMILY = "'Roboto Mono', monospace"
    const LABEL_STYLE = {
        "text-wrap": "wrap",
        "text-max-width": "420px",
        "font-family": LABEL_FONT_FAMILY,
        "font-size": NATIVE_LABEL_PX,
        "font-weight": "bold",
        "color": "#0a0a0a",
        "text-valign": "bottom",
        "text-halign": "center",
        "text-margin-y": 4,
        "text-background-color": "#ffffff",
        "text-background-opacity": 0.85,
        "text-background-padding": 2,
    }
    // Note-box font. Declared explicitly so the canvas text measurement in boxDimensions()
    // wraps and sizes the box exactly the way Cytoscape lays the label out (same family / size /
    // line-height). Both Cytoscape and our measurement use the browser's canvas text engine, so
    // an identical font string ⇒ identical metrics ⇒ identical wrapped line count.
    const BOX_FONT_PX = 18
    const BOX_LINE_HEIGHT = 1.2
    const BOX_FONT = `${BOX_FONT_PX}px Helvetica, Arial, sans-serif`
    // grey rounded box, visually identical to the .noteContent overlay
    const BOX_STYLE = {
        "shape": "round-rectangle",
        "background-color": "#eeeeee",
        "background-opacity": 1,
        "background-image": "none",
        "border-width": 2,
        "border-color": "#808080",
        "text-valign": "center",
        "text-halign": "center",
        "text-wrap": "wrap",
        "color": "#000000",
        "font-family": "Helvetica, Arial, sans-serif",
        "font-size": BOX_FONT_PX,
        "line-height": BOX_LINE_HEIGHT,
        "z-index": 9999,
    }
    // every property we ever set on a *real* node, so we can revert exactly these
    const REVERT_PROPS = [...new Set([
        ...Object.keys(LABEL_STYLE),
        ...Object.keys(BOX_STYLE),
        "label", "width", "height", "text-margin-x",
    ])].join(" ")

    // Lay out a note box. The note is plain textarea text (Enter = "\n"; long lines soft-wrap in
    // the overlay's white-space:pre-line). We hard-wrap it here at word boundaries — measured with
    // the exact box font — and join with real "\n", so every visual line is an explicit newline.
    // The box height is then just the line count (deterministic), instead of depending on whether
    // Cytoscape re-wraps the same way (which under-sized multi-line notes to one line before).
    const _measureCtx = document.createElement("canvas").getContext("2d")
    const MAX_TEXT_PX = 664   // wrap width for the text (≈ 680 box − 16 padding)
    const boxLayout = (rawText) => {
        _measureCtx.font = BOX_FONT
        const measure = (s) => _measureCtx.measureText(s).width
        const wrap = (line) => {
            const words = line.split(/\s+/).filter(Boolean)
            if (!words.length) return [""]
            const out = []
            let current = ""
            for (const word of words) {
                const candidate = current ? `${current} ${word}` : word
                if (!current || measure(candidate) <= MAX_TEXT_PX) {
                    current = candidate            // fits, or a single over-long word on its own line
                } else {
                    out.push(current)
                    current = word
                }
            }
            out.push(current)
            return out
        }
        const lines = String(rawText).split("\n").reduce((acc, line) => acc.concat(wrap(line)), [])
        const label = lines.join("\n")
        const longestPx = lines.reduce((max, line) => Math.max(max, measure(line)), 0)
        const width = Math.min(680, Math.max(150, Math.ceil(longestPx) + 26))
        const height = Math.max(40, Math.ceil(lines.length * BOX_FONT_PX * BOX_LINE_HEIGHT) + 18)
        return {width, height, label}
    }

    // The address label is prefixed with a small icon in the overlay: a grey address-card for a
    // labelled identity, a person icon for an owner, nothing for a bare address. Redraw that icon
    // just left of the (measured) label text so the exported label reads like the live one.
    // Must be called AFTER the labels are applied (post-batch): reads the node's label bounding box
    // so the icon lands exactly at the label's left edge — no font/measurement guesswork.
    const labelIcon = (node) => {
        const addressData = node.data("typeSpecific")?.addressData ?? {}
        const identity = addressData.label || addressData.identities?.[0]?.label
        const owner = addressData.owner?.name || addressData.owners?.[0]?.name
        if (!identity && !owner) return null            // bare address -> no icon, matching the overlay
        const {glyph, fontFamily, fontWeight} = probeIcon(identity ? "fas fa-address-card" : "fas fa-user")
        if (!glyph) return null
        // The label was shifted right by ICON_MARGIN (in the loop) so icon+text read as one centred
        // unit; bb reflects that shifted label, so the icon lands just left of it.
        const bb = node.boundingBox({includeLabels: true, includeOverlays: false})
        const nodeBottom = node.position().y + node.height() / 2
        return {
            id: `${NOTE_FLAG}::licon::${node.id()}`,
            x: bb.x1 - ICON_GAP - ICON_SIZE / 2,        // just left of the shifted label's left edge
            y: (nodeBottom + 4 + bb.y2) / 2,            // vertical centre of the label line (4 = text-margin-y)
            size: ICON_SIZE,
            glyph, fontFamily, fontWeight,
        }
    }

    const ExportPng = (cy, fileName = "graph") => {
        if (!cy || cy.elements().empty()) return

        // clear any helper nodes left behind by a previous (interrupted) export
        cy.nodes(`[?${NOTE_FLAG}]`).remove()

        const addedIds = []
        try {
            const pendingNotes = []
            const pendingBadges = []
            const pendingLabelIconNodes = []
            cy.batch(() => {
                cy.nodes().forEach((node) => {
                    if (node.data(NOTE_FLAG)) return

                    // standalone note nodes are DOM-rendered (invisible on canvas) -> draw a box
                    if (node.hasClass("note")) {
                        const {width, height, label} = boxLayout(stripHtml(node.data("text")))
                        node.style({...BOX_STYLE, label, width, height, "text-max-width": width - 16})
                        return
                    }

                    node.style({...LABEL_STYLE, label: nodeLabel(node)})

                    const badge = categoryBadge(node)
                    if (badge) pendingBadges.push(badge)

                    const ad = node.data("typeSpecific")?.addressData
                    if (ad && (ad.label || ad.identities?.[0]?.label || ad.owner?.name || ad.owners?.[0]?.name)) {
                        // shift the label right so the icon (added later) + text stay centred on the node
                        node.style("text-margin-x", ICON_MARGIN)
                        pendingLabelIconNodes.push(node)
                    }

                    const annotation = node.data("annotation")
                    if (annotation && String(annotation).trim()) {
                        const position = node.position()
                        pendingNotes.push({
                            id: `${NOTE_FLAG}::${node.id()}`,
                            label: String(annotation).trim(),
                            x: position.x,
                            y: position.y - (node.height() / 2) - 30,
                        })
                    }
                })
            })

            pendingNotes.forEach((note) => {
                if (!cy.getElementById(note.id).empty()) return
                const {width, height, label} = boxLayout(note.label)
                const box = cy.add({
                    group: "nodes",
                    data: {id: note.id, label, [NOTE_FLAG]: true},
                    position: {x: note.x, y: note.y - height / 2},
                    selectable: false,
                    grabbable: false,
                })
                box.style({...BOX_STYLE, label, width, height, "text-max-width": width - 16})
                addedIds.push(note.id)
            })

            pendingBadges.forEach((badge) => {
                if (!cy.getElementById(badge.id).empty()) return
                const circle = cy.add({
                    group: "nodes",
                    data: {id: badge.id, [NOTE_FLAG]: true},
                    position: {x: badge.x, y: badge.y},
                    selectable: false,
                    grabbable: false,
                })
                circle.style({
                    "shape": "ellipse",
                    "background-color": badge.bg,
                    "background-opacity": 1,
                    "background-image": "none",
                    "border-width": 1,
                    "border-color": "#808080",
                    "width": badge.size,
                    "height": badge.size,
                    "label": badge.glyph,
                    "font-family": badge.fontFamily,
                    "font-weight": badge.fontWeight,
                    "font-size": Math.round(badge.size * 0.71),  // 20px glyph in a 28px circle, as in the overlay
                    "color": badge.fg,
                    "text-valign": "center",
                    "text-halign": "center",
                    "z-index": 10000,
                })
                addedIds.push(badge.id)
            })

            pendingLabelIconNodes.forEach((node) => {
                const icon = labelIcon(node)
                if (!icon || !cy.getElementById(icon.id).empty()) return
                const el = cy.add({
                    group: "nodes",
                    data: {id: icon.id, [NOTE_FLAG]: true},
                    position: {x: icon.x, y: icon.y},
                    selectable: false,
                    grabbable: false,
                })
                el.style({
                    "shape": "rectangle",
                    "background-opacity": 0,
                    "background-image": "none",
                    "border-width": 0,
                    "width": icon.size,
                    "height": icon.size,
                    "label": icon.glyph,
                    "font-family": icon.fontFamily,
                    "font-weight": icon.fontWeight,
                    "font-size": icon.size,
                    "color": "#808080",
                    "text-valign": "center",
                    "text-halign": "center",
                    "z-index": 10000,
                })
                addedIds.push(icon.id)
            })

            // Render at the readability scale, clamped down so a single side never exceeds
            // MAX_SIDE and total area stays under MAX_AREA (otherwise cy.png returns empty).
            const bb = cy.elements().boundingBox()
            const fitScale = Math.min(
                MAX_SIDE / bb.w,
                MAX_SIDE / bb.h,
                Math.sqrt(MAX_AREA / (bb.w * bb.h)),
            )
            let scale = Math.min(READABILITY_SCALE, fitScale)
            if (!Number.isFinite(scale) || scale <= 0) scale = 1

            // Only warn in the genuinely degraded case: the graph is so large it had to be shrunk
            // below its on-screen size (labels smaller than native). Moderate down-scaling from the
            // readability target still renders crisp, readable text, so it isn't worth a warning.
            if (scale < 1) {
                store.dispatch("warning",
                    "This graph is very large, so labels in the exported image are small. " +
                    "Remove some nodes for a clearer image.")
            }

            const blob = cy.png({full: true, bg: "#ffffff", scale, output: "blob"})
            if (blob && blob.size > 0) {
                saveAs(blob, `${fileName}.png`)
            } else {
                // cy.png returns an empty blob when the canvas exceeds the browser limit.
                store.dispatch("error",
                    "Couldn't export — this graph is too large to fit in a single image. Remove some nodes and try again.")
            }
        } finally {
            // always revert: drop EVERY helper node (by flag, so a throw mid-add can't orphan one
            // on the live graph) and clear the inline styles we set on real nodes.
            cy.nodes(`[?${NOTE_FLAG}]`).remove()
            cy.nodes().removeStyle(REVERT_PROPS)
        }
    }

    return {ExportPng}
}
